import React, { FC, useEffect, useState } from 'react'
import { cn } from 'ethcss'
import Icon from 'blocks.simple/icon/icon'
import styles from './templateEditor__numberStepper.jcss'

type Props = {
    label: string
    value: number
    onChange: (value: number) => void
    step?: number
    min?: number
    max?: number
    disabled?: boolean
    className?: string
}

const clamp = (value: number, min?: number, max?: number) => {
    let next = value
    if (typeof min === 'number') {
        next = Math.max(min, next)
    }
    if (typeof max === 'number') {
        next = Math.min(max, next)
    }
    return next
}

export const TemplateEditorNumberStepper: FC<Props> = ({
    label,
    value,
    onChange,
    step = 1,
    min,
    max,
    disabled,
    className,
}) => {
    const [draft, setDraft] = useState(String(Math.round(value)))
    const [focused, setFocused] = useState(false)

    useEffect(() => {
        if (!focused) {
            setDraft(String(Math.round(value)))
        }
    }, [value, focused])

    const commit = (raw: string) => {
        const parsed = Number.parseInt(raw, 10)
        if (!Number.isFinite(parsed)) {
            setDraft(String(Math.round(value)))
            return
        }
        const next = clamp(Math.round(parsed), min, max)
        setDraft(String(next))
        if (next !== Math.round(value)) {
            onChange(next)
        }
    }

    const nudge = (direction: 1 | -1) => {
        const next = clamp(Math.round(value) + direction * step, min, max)
        setDraft(String(next))
        if (next !== Math.round(value)) {
            onChange(next)
        }
    }

    return (
        <div className={cn(styles.root, className)}>
            <div className={styles.label}>{label}</div>
            <div className={cn(styles.control, focused && styles.controlFocused, disabled && styles.controlDisabled)}>
                <input
                    className={styles.input}
                    type="text"
                    inputMode="numeric"
                    value={draft}
                    disabled={disabled}
                    onFocus={() => setFocused(true)}
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={() => {
                        setFocused(false)
                        commit(draft)
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.currentTarget.blur()
                            return
                        }
                        if (event.key === 'ArrowUp') {
                            event.preventDefault()
                            nudge(1)
                            return
                        }
                        if (event.key === 'ArrowDown') {
                            event.preventDefault()
                            nudge(-1)
                        }
                    }}
                />
                <div className={styles.steppers}>
                    <button
                        type="button"
                        className={styles.stepBtn}
                        tabIndex={-1}
                        disabled={disabled}
                        aria-label="+"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => nudge(1)}
                    >
                        <Icon type="triangleUp" size={8} />
                    </button>
                    <button
                        type="button"
                        className={styles.stepBtn}
                        tabIndex={-1}
                        disabled={disabled}
                        aria-label="-"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => nudge(-1)}
                    >
                        <Icon type="triangleDown" size={8} />
                    </button>
                </div>
            </div>
        </div>
    )
}
